// Enhanced visualization system for living neural networks with fractal patterns and energy flow

import { generateLivingFractalPattern } from './fractalUtils';
import { generatePsychicProfile } from './PsychicPotential';
import { Limb, LimbSegment, LimbType } from './limbSystem';

export class VisualizationLayer {
    _lastBgFrame: any;
    _cachedGrad: CanvasGradient | null = null;
    _cachedGradWidth: number = 0;
    _cachedGradHeight: number = 0;
    _fractalPatterns: any[] = [];
    _energySurgeTrails: { x: number; y: number; intensity: number; time: number }[] = [];

    // Render neural network graph with wave visualization and energy flow animation
    renderNetworkGraph(network: any) {
        if (!network || !network.nodes || !network.edges) return;
        let canvas = document.getElementById('visualization-background-canvas') as HTMLCanvasElement;
        if (!canvas) {
            canvas = document.createElement('canvas');
            canvas.id = 'visualization-background-canvas';
            canvas.className = 'visualization-overlay-canvas';
            canvas.style.position = 'fixed';
            canvas.style.left = '0';
            canvas.style.top = '0';
            canvas.style.width = '100vw';
            canvas.style.height = '100vh';
            canvas.style.pointerEvents = 'none';
            canvas.style.zIndex = '-2'; // Behind simulation canvas
            canvas.style.background = 'transparent';
            canvas.style.display = 'block';
            document.body.insertBefore(canvas, document.body.firstChild);
        }
        // Only resize if needed
        if (canvas.width !== window.innerWidth || canvas.height !== window.innerHeight) {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        }
        canvas.style.display = 'block';

        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // --- Render animated background illumination (draw only every 10 frames) ---
        if (!this._lastBgFrame || Date.now() - this._lastBgFrame > 160) {
            const t = Date.now() / 900;
            // Cache gradient if size changed
            if (
                !this._cachedGrad ||
                this._cachedGradWidth !== canvas.width ||
                this._cachedGradHeight !== canvas.height
            ) {
                this._cachedGrad = ctx.createRadialGradient(
                    canvas.width / 2, canvas.height / 2, 120 + Math.sin(t) * 40,
                    canvas.width / 2, canvas.height / 2, Math.max(canvas.width, canvas.height) / 1.2
                );
                this._cachedGrad.addColorStop(0, `rgba(255,255,220,0.08)`);
                this._cachedGrad.addColorStop(0.4, `rgba(160,120,255,0.05)`);
                this._cachedGrad.addColorStop(1, `rgba(80,0,120,0.01)`);
                this._cachedGradWidth = canvas.width;
                this._cachedGradHeight = canvas.height;
            }
            ctx.save();
            ctx.globalAlpha = 0.32; // Lower alpha for perf
            ctx.fillStyle = this._cachedGrad;
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.restore();
            this._lastBgFrame = Date.now();
        }

        // --- Render shockwave pulses (wave visualization) ---
        if (Array.isArray(network.waves)) {
            for (const wave of network.waves) {
                if (!wave.active) continue;
                ctx.save();
                ctx.beginPath();
                ctx.arc(wave.x, wave.y, wave.radius, 0, 2 * Math.PI);
                ctx.strokeStyle = wave.color || `rgba(255,255,80,0.10)`;
                ctx.lineWidth = 3; // Lower for perf
                ctx.globalAlpha = 0.12;
                ctx.shadowColor = '#ffe066';
                ctx.shadowBlur = 8; // Lower for perf
                ctx.stroke();
                ctx.restore();
                wave.radius += wave.speed;
                if (wave.radius > 340) wave.active = false;
            }
        }

        // --- Fluidic wave physics visualization ---
        for (const node of network.nodes) {
            // Note: Collision detection removed - physics system handles node positioning
            if (node.waveAmplitude && node.waveFrequency) {
                ctx.save();
                let hue = 0;
                if (typeof node.color === 'string') {
                    const match = node.color.match(/hsl\((\d+)/);
                    if (match) hue = parseInt(match[1]);
                }
                const freq = node.waveFrequency;
                const amp = Math.abs(node.waveAmplitude);
                const baseRadius = 18 + freq * 80 + amp * 12;
                const alpha = 0.10 + Math.min(0.14, amp * 0.10);
                ctx.beginPath();
                ctx.arc(node.x, node.y, baseRadius, 0, 2 * Math.PI);
                ctx.strokeStyle = `hsla(${hue},80%,60%,${alpha})`;
                ctx.lineWidth = 1.5;
                ctx.shadowColor = `hsla(${hue},80%,70%,0.2)`;
                ctx.shadowBlur = 4;
                ctx.globalAlpha = alpha;
                ctx.stroke();
                ctx.restore();

                // --- Sparky glow for sun absorption ---
                if (node.sunAbsorbing) {
                    ctx.save();
                    const sparkT = Date.now() / 180;
                    const sparkRadius = baseRadius + 12 + Math.sin(sparkT + node.id) * 6;
                    ctx.beginPath();
                    ctx.arc(node.x, node.y, sparkRadius, 0, 2 * Math.PI);
                    ctx.strokeStyle = `rgba(255,255,80,0.25)`;
                    ctx.lineWidth = 2.5;
                    ctx.shadowColor = '#ffe066';
                    ctx.shadowBlur = 12;
                    ctx.globalAlpha = 0.22 + 0.10 * Math.abs(Math.sin(sparkT + node.id * 0.3));
                    ctx.stroke();
                    ctx.restore();
                }
            }
        }

        // --- Animated energy flow along edges ---
        for (const edge of network.edges) {
            const a = network.nodes[edge.from], b = network.nodes[edge.to];
            if (!a || !b) continue;
            ctx.save();
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            const match = a.color.match(/hsl\((\d+)/);
            const edgeHue = (parseInt(match ? match[1] : '0') + 240) % 360;
            // Make edge lines thicker and clearer
            ctx.strokeStyle = `hsl(${edgeHue},100%,65%)`;
            ctx.lineWidth = 1.5; // Increased thickness
            ctx.globalAlpha = 0.85; // More visible
            ctx.shadowColor = `hsl(${edgeHue},100%,80%)`;
            ctx.shadowBlur = 8;
            ctx.stroke();
            ctx.restore();
        }
        
        // Render creature limbs
        this.renderLimbs(network);
    }

    // Render creature limbs
    renderLimbs(network: any) {
        if (!network || !network.limbSystem) return;
        
        const limbs = network.limbSystem.getLimbs();
        if (limbs.length === 0) return;
        
        let canvas = document.getElementById('visualization-background-canvas') as HTMLCanvasElement;
        if (!canvas) return;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        
        ctx.save();
        
        for (const limb of limbs) {
            this.renderLimb(ctx, limb, network);
        }
        
        ctx.restore();
    }
    
    // Render individual limb
    renderLimb(ctx: CanvasRenderingContext2D, limb: Limb, network: any) {
        if (limb.segments.length === 0) return;
        
        ctx.save();
        
        // Set limb-specific rendering properties
        const limbColor = this.getLimbColor(limb);
        const limbAlpha = this.getLimbAlpha(limb);
        const limbGlow = this.getLimbGlow(limb);
        
        ctx.globalAlpha = limbAlpha;
        ctx.strokeStyle = limbColor;
        ctx.fillStyle = limbColor;
        ctx.lineWidth = this.getLimbLineWidth(limb);
        
        // Add glow effect for active limbs
        if (limbGlow > 0) {
            ctx.shadowColor = limbColor;
            ctx.shadowBlur = limbGlow;
        }
        
        // Render limb segments
        for (let i = 0; i < limb.segments.length; i++) {
            const segment = limb.segments[i];
            this.renderLimbSegment(ctx, segment, limb, i);
        }
        
        // Render limb energy effects
        if (limb.state === 'active' && limb.currentEnergy > limb.energyCapacity * 0.5) {
            this.renderLimbEnergyEffects(ctx, limb);
        }
        
        ctx.restore();
    }
    
    // Render individual limb segment
    renderLimbSegment(ctx: CanvasRenderingContext2D, segment: LimbSegment, limb: Limb, index: number) {
        const time = Date.now() / 1000;
        const breathing = Math.sin(time * 2 + segment.id * 0.1) * 0.1;
        const energyPulse = Math.sin(time * 4 + segment.id * 0.2) * 0.2;
        
        // Calculate segment position with breathing and energy effects
        const x = segment.x + breathing * 2;
        const y = segment.y + breathing * 1.5;
        const z = segment.z + energyPulse * 3;
        
        // Render segment as a line or circle based on limb type
        if (limb.type === 'fractal_branch' || limb.type === 'neural_extension') {
            // Render as connected line segments
            if (index > 0) {
                const prevSegment = limb.segments[index - 1];
                ctx.beginPath();
                ctx.moveTo(prevSegment.x, prevSegment.y);
                ctx.lineTo(x, y);
                ctx.stroke();
            }
        } else if (limb.type === 'psychic_manifest' || limb.type === 'mystical_tendril') {
            // Render as glowing orbs
            ctx.beginPath();
            ctx.arc(x, y, segment.thickness + energyPulse * 2, 0, Math.PI * 2);
            ctx.fill();
        } else if (limb.type === 'wave_resonance' || limb.type === 'energy_conduit') {
            // Render as wavy lines
            this.renderWavySegment(ctx, segment, x, y, time);
        } else {
            // Default rendering as simple lines
            ctx.beginPath();
            ctx.moveTo(segment.x, segment.y);
            ctx.lineTo(x, y);
            ctx.stroke();
        }
        
        // Add segment-specific effects
        if (segment.energy > 70) {
            this.renderSegmentEnergyEffect(ctx, segment, x, y);
        }
    }
    
    // Render wavy segment for wave-based limbs
    renderWavySegment(ctx: CanvasRenderingContext2D, segment: LimbSegment, x: number, y: number, time: number) {
        const waveCount = 3;
        const waveAmplitude = 5;
        const waveLength = segment.length / waveCount;
        
        ctx.beginPath();
        ctx.moveTo(segment.x, segment.y);
        
        for (let i = 1; i <= waveCount; i++) {
            const t = i / waveCount;
            const waveX = segment.x + (x - segment.x) * t;
            const waveY = segment.y + (y - segment.y) * t + Math.sin(time * 2 + t * Math.PI * 2) * waveAmplitude;
            ctx.lineTo(waveX, waveY);
        }
        
        ctx.stroke();
    }
    
    // Render energy effects for active limbs
    renderLimbEnergyEffects(ctx: CanvasRenderingContext2D, limb: Limb) {
        const time = Date.now() / 1000;
        
        for (const segment of limb.segments) {
            if (segment.energy > 60) {
                // Energy particles around segment
                const particleCount = Math.floor(segment.energy / 20);
                for (let i = 0; i < particleCount; i++) {
                    const angle = (time * 2 + i * Math.PI * 2 / particleCount) % (Math.PI * 2);
                    const radius = 15 + Math.sin(time * 3 + i) * 5;
                    const particleX = segment.x + Math.cos(angle) * radius;
                    const particleY = segment.y + Math.sin(angle) * radius;
                    
                    ctx.save();
                    ctx.globalAlpha = 0.6;
                    ctx.fillStyle = this.getLimbColor(limb);
                    ctx.beginPath();
                    ctx.arc(particleX, particleY, 2, 0, Math.PI * 2);
                    ctx.fill();
                    ctx.restore();
                }
            }
        }
    }
    
    // Render energy effect for individual segment
    renderSegmentEnergyEffect(ctx: CanvasRenderingContext2D, segment: LimbSegment, x: number, y: number) {
        const time = Date.now() / 1000;
        const pulse = Math.sin(time * 4 + segment.id * 0.1) * 0.5 + 0.5;
        
        ctx.save();
        ctx.globalAlpha = pulse * 0.3;
        ctx.fillStyle = segment.color;
        ctx.beginPath();
        ctx.arc(x, y, segment.thickness * 2 * pulse, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
    }
    
    // Get limb color based on type and state
    getLimbColor(limb: Limb): string {
        const baseColors: Record<LimbType, string> = {
            'fractal_branch': '#ff6b6b',
            'psychic_manifest': '#9b59b6',
            'linguistic_appendage': '#3498db',
            'wave_resonance': '#1abc9c',
            'energy_conduit': '#f39c12',
            'sensing_probe': '#e74c3c',
            'vocal_modifier': '#2ecc71',
            'neural_extension': '#34495e',
            'defensive_spike': '#e67e22',
            'mystical_tendril': '#8e44ad',
            'temporal_phase': '#16a085',
            'adaptive_structure': '#27ae60'
        };
        
        let color = baseColors[limb.type] || '#ffffff';
        
        // Modify color based on limb state
        if (limb.state === 'active') {
            color = this.brightenColor(color, 0.3);
        } else if (limb.state === 'dormant') {
            color = this.darkenColor(color, 0.5);
        } else if (limb.state === 'growing') {
            color = this.saturateColor(color, 0.2);
        }
        
        return color;
    }
    
    // Get limb alpha based on state and energy
    getLimbAlpha(limb: Limb): number {
        let alpha = 0.7;
        
        if (limb.state === 'active') {
            alpha = 0.9;
        } else if (limb.state === 'dormant') {
            alpha = 0.3;
        } else if (limb.state === 'growing') {
            alpha = 0.8;
        } else if (limb.state === 'atrophying') {
            alpha = 0.2;
        }
        
        // Adjust based on energy level
        const energyFactor = limb.currentEnergy / limb.energyCapacity;
        alpha *= 0.5 + energyFactor * 0.5;
        
        return Math.max(0.1, Math.min(1, alpha));
    }
    
    // Get limb glow intensity
    getLimbGlow(limb: Limb): number {
        if (limb.state !== 'active') return 0;
        
        const energyFactor = limb.currentEnergy / limb.energyCapacity;
        return energyFactor * 15;
    }
    
    // Get limb line width
    getLimbLineWidth(limb: Limb): number {
        const baseWidth = 2;
        const energyFactor = limb.currentEnergy / limb.energyCapacity;
        return baseWidth + energyFactor * 2;
    }
    
    // Color utility functions
    brightenColor(color: string, factor: number): string {
        // Simple brightening - in a real implementation, you'd parse HSL and adjust
        return color;
    }
    
    darkenColor(color: string, factor: number): string {
        // Simple darkening - in a real implementation, you'd parse HSL and adjust
        return color;
    }
    
    saturateColor(color: string, factor: number): string {
        // Simple saturation - in a real implementation, you'd parse HSL and adjust
        return color;
    }

    // Render living fractal patterns around nodes
    renderLivingFractalPatterns(ctx: CanvasRenderingContext2D, network: any) {
        try {
            const fractalData = generateLivingFractalPattern(network);
            ctx.save();
            ctx.globalAlpha = 0.3 * fractalData.lifeForce;
            
            for (const pattern of fractalData.patterns) {
                if (pattern.intensity > 0.1) {
                    ctx.fillStyle = pattern.color;
                    ctx.beginPath();
                    ctx.arc(pattern.x, pattern.y, pattern.intensity * 3, 0, Math.PI * 2);
                    ctx.fill();
                }
            }
            ctx.restore();
        } catch (error) {
            console.warn('Fractal pattern rendering error:', error);
        }
    }

    // Render energy surge trails
    renderEnergySurgeTrails(ctx: CanvasRenderingContext2D, network: any) {
        if (!network.energySurgeActive || !network.crystalHeartId) return;
        
        const heart = network.nodes[network.crystalHeartId];
        if (!heart) return;
        
        ctx.save();
        ctx.globalAlpha = network.energySurgeIntensity * 0.4;
        
        // Render energy trails from heart to connected nodes
        for (const edge of network.edges) {
            if (edge.from === network.crystalHeartId) {
                const targetNode = network.nodes[edge.to];
                if (targetNode && !targetNode.corroded) {
                    ctx.strokeStyle = `hsl(290, 100%, ${70 + network.energySurgeIntensity * 30}%)`;
                    ctx.lineWidth = 2 + network.energySurgeIntensity * 3;
                    ctx.beginPath();
                    ctx.moveTo(heart.x, heart.y);
                    ctx.lineTo(targetNode.x, targetNode.y);
                    ctx.stroke();
                    
                    // Add energy pulse at target
                    ctx.fillStyle = `hsl(290, 100%, ${80 + network.energySurgeIntensity * 20}%)`;
                    ctx.beginPath();
                    ctx.arc(targetNode.x, targetNode.y, network.energySurgeIntensity * 8, 0, Math.PI * 2);
                    ctx.fill();
                }
            }
        }
        ctx.restore();
    }

    // Render enhanced wave patterns with interference
    renderEnhancedWaves(ctx: CanvasRenderingContext2D, network: any) {
        if (!network.waves || network.waves.length === 0) return;
        
        ctx.save();
        
        for (const wave of network.waves) {
            if (!wave.active) continue;
            
            // Base wave rendering
            ctx.globalAlpha = 0.6 * (1 - wave.radius / 400);
            ctx.strokeStyle = wave.color || '#ffffff';
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.arc(wave.x, wave.y, wave.radius, 0, Math.PI * 2);
            ctx.stroke();
            
            // Add interference patterns for special waves
            if (wave.originId === -1) {
                ctx.globalAlpha = 0.4;
                ctx.strokeStyle = `hsl(${(Date.now() / 10) % 360}, 90%, 70%)`;
                ctx.lineWidth = 1;
                ctx.beginPath();
                ctx.arc(wave.x, wave.y, wave.radius * 0.8, 0, Math.PI * 2);
                ctx.stroke();
            }
        }
        
        ctx.restore();
    }

    // Render crystal heart life force visualization
    renderCrystalHeartLifeForce(ctx: CanvasRenderingContext2D, network: any) {
        if (!network.crystalHeartId) return;
        
        const heart = network.nodes[network.crystalHeartId];
        if (!heart) return;
        
        ctx.save();
        
        // Heart's pulsing aura
        const time = network.step / 60;
        const humIntensity = Math.sin(time * network.heartHumFrequency * 0.1) * 0.5 + 0.5;
        const auraRadius = 30 + humIntensity * 20;
        
        // Outer aura
        ctx.globalAlpha = 0.2 * humIntensity;
        ctx.fillStyle = `hsl(290, 100%, ${70 + humIntensity * 20}%)`;
        ctx.beginPath();
        ctx.arc(heart.x, heart.y, auraRadius, 0, Math.PI * 2);
        ctx.fill();
        
        // Inner life force
        ctx.globalAlpha = 0.6 * network.heartLifeForce;
        ctx.fillStyle = `hsl(290, 100%, 80%)`;
        ctx.beginPath();
        ctx.arc(heart.x, heart.y, 15 + humIntensity * 10, 0, Math.PI * 2);
        ctx.fill();
        
        // Energy field lines
        ctx.globalAlpha = 0.3;
        ctx.strokeStyle = `hsl(290, 80%, 70%)`;
        ctx.lineWidth = 1;
        for (let i = 0; i < 8; i++) {
            const angle = (i * Math.PI / 4) + time * 0.5;
            const startRadius = 20;
            const endRadius = auraRadius;
            ctx.beginPath();
            ctx.moveTo(
                heart.x + Math.cos(angle) * startRadius,
                heart.y + Math.sin(angle) * startRadius
            );
            ctx.lineTo(
                heart.x + Math.cos(angle) * endRadius,
                heart.y + Math.sin(angle) * endRadius
            );
            ctx.stroke();
        }
        
        ctx.restore();
    }
}